import { useWelcomeOffer, WelcomePrice, discountedPrice } from '../offers/WelcomeOfferContext';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { userService } from '../api/userService';
import { subscriptionService } from '../api/subscriptionService';
import { paymentMethodService } from '../api/paymentMethodService';
import { detectCountry, getDisplayCurrency, getAllPlanPrices, getPlanPrice, getCachedCountry } from '../utils/pricing';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useI18n, rich } from '../i18n';

// Icons
const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CreditCardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const UsersIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CARD_BRAND_LABELS = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'Amex',
  discover: 'Discover',
  diners: 'Diners',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  unknown: 'Card',
};

const GlobeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const LoadingSpinner = () => (
  <div className="w-5 h-5 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
);

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, locale } = useI18n();
  
  // Profile state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [authProvider, setAuthProvider] = useState('email');
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState(null);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [isManagingSubscription, setIsManagingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(true);
  const [paymentMethodError, setPaymentMethodError] = useState(null);
  const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(false);
  const [removingPaymentMethodId, setRemovingPaymentMethodId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [setupSuccess, setSetupSuccess] = useState(false);
  
  // Subscription confirmation modal
  const [showSubscriptionConfirmModal, setShowSubscriptionConfirmModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [confirmedOfferPercent, setConfirmedOfferPercent] = useState(null);
  const [pricingCurrency, setPricingCurrency] = useState(() =>
    getDisplayCurrency({ geoCountry: getCachedCountry() })
  );
  const { offer: welcomeOffer } = useWelcomeOffer();
  const planPrices = getAllPlanPrices(pricingCurrency);

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const profile = await userService.getMe();
        if (profile) {
          setFirstName(profile.first_name || '');
          setLastName(profile.last_name || '');
          setEmail(profile.email || '');
          setPhoneNumber(profile.phone_number || '');
          setAuthProvider(profile.auth_provider || 'email');
        }
      } catch (err) {
        console.error('Failed to fetch profile:', err);
        setProfileError('Failed to load profile');
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

  // Fetch subscription data + resolve display currency (billing_currency wins over geo)
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        setIsLoadingSubscription(true);
        const [sub, geoCountry] = await Promise.all([
          subscriptionService.getMySubscription(),
          detectCountry(),
        ]);
        setSubscription(sub);
        setPricingCurrency(getDisplayCurrency({
          billingCurrency: sub?.billing_currency,
          geoCountry,
        }));
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
        setSubscriptionError('Failed to load subscription');
        const geoCountry = await detectCountry().catch(() => null);
        setPricingCurrency(getDisplayCurrency({ geoCountry }));
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    fetchSubscription();
  }, []);

  // Fetch payment methods
  const fetchPaymentMethods = useCallback(async () => {
    try {
      setIsLoadingPaymentMethods(true);
      setPaymentMethodError(null);
      const methods = await paymentMethodService.list();
      setPaymentMethods(methods || []);
    } catch (err) {
      console.error('Failed to fetch payment methods:', err);
      setPaymentMethodError('Failed to load payment methods');
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  }, []);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  // Detect ?setup=success query param (redirect back from Stripe)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('setup') === 'success') {
      setSetupSuccess(true);
      // Clean the URL without reloading
      navigate('/settings', { replace: true });
      // Refresh payment methods after a short delay to allow Stripe webhook processing
      setTimeout(() => fetchPaymentMethods(), 1500);
      // Auto-dismiss success message
      setTimeout(() => setSetupSuccess(false), 5000);
    }
  }, [location.search, navigate, fetchPaymentMethods]);

  // Save profile
  const handleSaveProfile = async () => {
    try {
      setIsSavingProfile(true);
      setProfileError(null);
      setProfileSaved(false);
      
      await userService.updateProfile({
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber || null
      });
      
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save profile:', err);
      setProfileError('Failed to save profile. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Manage subscription (open Stripe portal)
  const handleManageSubscription = async () => {
    try {
      setIsManagingSubscription(true);
      setSubscriptionError(null);
      const result = await subscriptionService.openPortal();
      if (result?.portal_url) {
        window.location.href = result.portal_url;
      } else {
        setSubscriptionError('Failed to open billing portal');
      }
    } catch (err) {
      console.error('Failed to open portal:', err);
      setSubscriptionError('Failed to open billing portal. Please try again.');
    } finally {
      setIsManagingSubscription(false);
    }
  };

  // Upgrade to plan - show confirmation modal first
  const handleUpgrade = (plan) => {
    setSelectedPlan(plan);
    setConfirmedOfferPercent(welcomeOffer?.plans.includes(plan) ? welcomeOffer.percent_off : null);
    setShowSubscriptionConfirmModal(true);
  };

  // Confirm subscription after user consent
  const handleConfirmSubscription = async () => {
    if (!selectedPlan) return;

    try {
      setIsManagingSubscription(true);
      setSubscriptionError(null);
      setShowSubscriptionConfirmModal(false);

      // Check if user has a saved payment method
      const hasPaymentMethod = paymentMethods.length > 0;

      if (hasPaymentMethod) {
        // Use saved payment method for direct subscription (no Stripe Checkout needed)
        const result = await subscriptionService.createDirectSubscription(selectedPlan, null, pricingCurrency, confirmedOfferPercent);
        if (result?.success) {
          // Refresh subscription data
          const sub = await subscriptionService.getMySubscription();
          setSubscription(sub);
          if (sub?.billing_currency) {
            setPricingCurrency(sub.billing_currency);
          }
          // Show success message
          setSubscriptionError(null);
          setSubscriptionSuccess(true);
          setTimeout(() => setSubscriptionSuccess(false), 5000);
        } else {
          setSubscriptionError('Failed to create subscription');
        }
      } else {
        // No saved payment method - redirect to Stripe Checkout
        const result = await subscriptionService.createCheckoutSession(selectedPlan, pricingCurrency, confirmedOfferPercent);
        if (result?.checkout_url) {
          window.location.href = result.checkout_url;
        } else {
          setSubscriptionError('Failed to create checkout session');
        }
      }
    } catch (err) {
      console.error('Failed to upgrade:', err);
      const message = err?.response?.data?.detail || 'Failed to start upgrade process. Please try again.';
      setSubscriptionError(message);
      window.dispatchEvent(new Event('subscription-changed'));
    } finally {
      setIsManagingSubscription(false);
      setSelectedPlan(null);
    }
  };

  // Add payment method (redirect to Stripe setup)
  const handleAddPaymentMethod = async () => {
    try {
      setIsAddingPaymentMethod(true);
      setPaymentMethodError(null);
      const result = await paymentMethodService.createSetup();
      if (result?.setup_url) {
        window.location.href = result.setup_url;
      } else {
        setPaymentMethodError('Failed to start payment method setup');
      }
    } catch (err) {
      console.error('Failed to create setup session:', err);
      setPaymentMethodError('Failed to add payment method. Please try again.');
    } finally {
      setIsAddingPaymentMethod(false);
    }
  };

  // Remove payment method
  const handleRemovePaymentMethod = async (pmId) => {
    try {
      setRemovingPaymentMethodId(pmId);
      setPaymentMethodError(null);
      await paymentMethodService.remove(pmId);
      setConfirmRemoveId(null);
      await fetchPaymentMethods();
    } catch (err) {
      console.error('Failed to remove payment method:', err);
      const message = err?.response?.data?.detail || 'Failed to remove payment method. Please try again.';
      setPaymentMethodError(message);
    } finally {
      setRemovingPaymentMethodId(null);
    }
  };

  // Set default payment method
  const handleSetDefault = async (pmId) => {
    try {
      setSettingDefaultId(pmId);
      setPaymentMethodError(null);
      await paymentMethodService.setDefault(pmId);
      await fetchPaymentMethods();
    } catch (err) {
      console.error('Failed to set default payment method:', err);
      setPaymentMethodError('Failed to set default payment method. Please try again.');
    } finally {
      setSettingDefaultId(null);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Format date
  const formatDate = (isoDate) => {
    if (!isoDate) return null;
    try {
      const date = new Date(isoDate);
      return date.toLocaleDateString(locale, { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return null;
    }
  };

  const plan = subscription?.plan || 'trial';
  const isStarter = plan === 'starter' && subscription?.status === 'active';
  const isPro = plan === 'pro' && subscription?.status === 'active';
  const isMax = plan === 'max' && subscription?.status === 'active';
  const isTrial = plan === 'trial';
  const hasPaidPlan = isStarter || isPro || isMax;

  // Helper to get plan details
  const getPlanDetails = (planType) => {
    const price = getPlanPrice(planType, pricingCurrency);
    const videos = { starter: 8, pro: 30, max: 100 }[planType] || 0;
    const names = { starter: 'Starter', pro: 'Pro', max: 'Max' };
    return {
      name: names[planType] || 'Unknown',
      price: confirmedOfferPercent ? discountedPrice(price, confirmedOfferPercent) : price.display,
      renewalPrice: price.display,
      videos,
    };
  };

  // Get default payment method
  const defaultPaymentMethod = paymentMethods.find(pm => pm.is_default) || paymentMethods[0];

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <header className="viewport-sticky-header sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeftIcon />
              <span className="font-semibold">{t('Back to Dashboard')}</span>
            </button>
            <img
              src={`${process.env.PUBLIC_URL}/Logo-ArabCap.svg`}
              alt="ArabCap"
              className="h-8 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Page Title */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Settings')}</h1>
          <p className="text-slate-500 mt-1">{t('Manage your account and subscription')}</p>
        </div>

        {/* Profile Section */}
        <section className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
              <UserIcon />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{t('Profile')}</h2>
              <p className="text-sm text-slate-500">{t('Your personal information')}</p>
            </div>
          </div>

          <div className="p-6">
            {isLoadingProfile ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Name Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {t('First Name')}
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#f73140] focus:ring-2 focus:ring-[#f73140]/10 outline-none transition-all text-slate-900"
                      placeholder={t('Enter your first name')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      {t('Last Name')}
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#f73140] focus:ring-2 focus:ring-[#f73140]/10 outline-none transition-all text-slate-900"
                      placeholder={t('Enter your last name')}
                    />
                  </div>
                </div>

                {/* Email Field (Read-only) */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    {t('Email')}
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={email}
                      disabled
                      className="w-full px-4 py-3 pr-12 rtl:pr-4 rtl:pl-28 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed"
                    />
                    <div className="absolute end-3 top-1/2 -translate-y-1/2">
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-white rounded-lg border border-slate-200">
                        <GoogleIcon />
                        <span className="text-xs font-semibold text-slate-500">Google</span>
                      </div>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-slate-400">
                    {t('Connected via Google. Email cannot be changed.')}
                  </p>
                </div>

                {/* Phone Number Field */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    {t('Phone Number')}
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+212 6XX XXX XXX"
                      className="w-full px-4 py-3 ps-12 rtl:pe-12 rtl:ps-4 rounded-xl border border-slate-200 focus:border-[#f73140] focus:ring-2 focus:ring-[#f73140]/10 outline-none transition-all text-slate-900"
                    />
                    <div className="absolute left-4 rtl:left-auto rtl:right-4 top-1/2 -translate-y-1/2 text-slate-400">
                      <PhoneIcon />
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-400">
                    <UsersIcon />
                    <span>{t('Join our community and receive exclusive offers')}</span>
                  </div>
                </div>

                {/* Error Message */}
                {profileError && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-100">
                    <p className="text-sm text-red-600">{t(profileError)}</p>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSavingProfile}
                    className={`px-6 py-3 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
                      profileSaved
                        ? 'bg-green-500 text-white'
                        : 'bg-[#f73140] text-white hover:bg-[#d62331]'
                    } ${isSavingProfile ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isSavingProfile ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('Saving...')}
                      </>
                    ) : profileSaved ? (
                      <>
                        <CheckIcon />
                        {t('Saved!')}
                      </>
                    ) : (
                      t('Save Changes')
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Subscription Section */}
        <section id="subscription" className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden scroll-mt-6">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
              <CreditCardIcon />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{t('Subscription')}</h2>
              <p className="text-sm text-slate-500">{t('Manage your plan and billing')}</p>
            </div>
          </div>

          <div className="p-6">
            {isLoadingSubscription ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Subscription Success Toast */}
                {subscriptionSuccess && (
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <CheckIcon />
                    </div>
                    <p className="text-sm font-semibold text-green-700">{t('Subscription activated successfully!')}</p>
                  </div>
                )}

                {/* Current Plan Status */}
                <div className={`p-6 rounded-2xl border-2 ${hasPaidPlan ? 'border-[#f73140] bg-[#f73140]/5' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                          isMax ? 'bg-purple-600 text-white' :
                          isPro ? 'bg-[#f73140] text-white' : 
                          isStarter ? 'bg-blue-500 text-white' : 
                          'bg-slate-200 text-slate-600'
                        }`}>
                          {isMax ? 'Max' : isPro ? 'Pro' : isStarter ? 'Starter' : t('Trial')}
                        </span>
                        <span className="flex items-center gap-1.5 text-green-600 text-sm font-semibold">
                          <span className="w-2 h-2 rounded-full bg-green-500" />
                          {t('Active')}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mt-3">
                        {t(isMax ? 'Max Plan' : isPro ? 'Pro Plan' : isStarter ? 'Starter Plan' : 'Trial')}
                      </h3>
                      {isTrial ? (
                        <div className="mt-2 space-y-1">
                          <p className="text-slate-600 font-semibold">
                            {t('{remaining} of {total} videos remaining (lifetime)', { remaining: subscription?.trial_videos_remaining || 0, total: subscription?.trial_videos_total || 1 })}
                          </p>
                          {subscription?.bonus_videos > 0 && (
                            <p className="text-sm text-amber-600 font-semibold">
                              {t('+ {count} bonus videos available', { count: subscription.bonus_videos })}
                            </p>
                          )}
                          {subscription?.bonus_expiring?.length > 0 && (
                            <p className="text-xs text-amber-600">
                              {t('{count} expire {date}', {
                                count: subscription.bonus_expiring[0].amount,
                                date: new Date(subscription.bonus_expiring[0].expires_at).toLocaleDateString(locale, { day: 'numeric', month: 'short' }),
                              })}
                            </p>
                          )}
                          <p className="text-sm text-slate-500">{t('2 min max per video')}</p>
                        </div>
                      ) : (
                        <p className="text-slate-500 mt-1">
                          {t('{count} videos per month • 2 min max per video', { count: isMax ? 100 : isPro ? 30 : 8 })}
                        </p>
                      )}
                    </div>
                    {hasPaidPlan && (
                      <div className="text-end">
                        <p className="text-3xl font-black text-slate-900">{t(isMax ? planPrices.max.display : isPro ? planPrices.pro.display : planPrices.starter.display)}</p>
                        <p className="text-sm text-slate-500">{t('/month')}</p>
                      </div>
                    )}
                  </div>

                  {/* Renewal Info */}
                  {subscription?.current_period_end && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-500">
                        {t('Renews on {date}', { date: formatDate(subscription.current_period_end) })}
                      </p>
                    </div>
                  )}
                </div>

                {/* Error Message */}
                {subscriptionError && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-100">
                    <p className="text-sm text-red-600">{t(subscriptionError)}</p>
                  </div>
                )}

                {/* Manage Subscription Button (for paid plans) */}
                {hasPaidPlan && (
                  <button
                    onClick={handleManageSubscription}
                    disabled={isManagingSubscription}
                    className="w-full px-6 py-3.5 rounded-xl font-bold text-sm border-2 border-[#f73140] text-[#f73140] hover:bg-[#f73140] hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t(isManagingSubscription ? 'Opening Portal...' : 'Manage Subscription')}
                  </button>
                )}

                {/* Available Plans (for trial/free users) */}
                {isTrial && (
                  <div className="space-y-4">
                    <div className="border-t border-slate-200 pt-4">
                      <h3 className="font-bold text-lg text-slate-900 mb-4">{t('Upgrade to unlock more')}</h3>
                    </div>

                    {/* Starter Plan Card */}
                    <div className="p-5 rounded-2xl border-2 border-blue-200 bg-blue-50/50">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-blue-500 text-white">
                            Starter
                          </span>
                          <h4 className="font-bold text-xl text-slate-900 mt-2">{t(planPrices.starter.display)}<span className="text-sm font-normal text-slate-500">{t('/month')}</span></h4>
                        </div>
                      </div>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('{count} videos per month', { count: 8 })}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('Up to 2 minutes per video')}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('All export formats')}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('Email support')}
                        </li>
                      </ul>
                      <button
                        onClick={() => handleUpgrade('starter')}
                        disabled={isManagingSubscription}
                        className="w-full px-6 py-3 rounded-xl font-bold text-sm bg-blue-500 text-white hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t(isManagingSubscription ? 'Processing...' : 'Subscribe to Starter')}
                      </button>
                    </div>

                    {/* Pro Plan Card */}
                    <div className="p-5 rounded-2xl border-2 border-[#f73140] bg-[#f73140]/5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-[#f73140] text-white">
                            Pro
                          </span>
                          <h4 className="font-bold text-xl text-slate-900 mt-2">{<WelcomePrice plan="pro" price={planPrices.pro} />}<span className="text-sm font-normal text-slate-500">{t('/month')}</span></h4>
                        </div>
                      </div>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('{count} videos per month', { count: 30 })}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('Up to 2 minutes per video')}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('All export formats')}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('Priority processing')}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('Priority support')}
                        </li>
                      </ul>
                      <button
                        onClick={() => handleUpgrade('pro')}
                        disabled={isManagingSubscription}
                        className="w-full px-6 py-3 rounded-xl font-bold text-sm bg-[#f73140] text-white hover:bg-[#f73140]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t(isManagingSubscription ? 'Processing...' : 'Subscribe to Pro')}
                      </button>
                    </div>

                    {/* Max Plan Card */}
                    <div className="p-5 rounded-2xl border-2 border-purple-600 bg-purple-50/50">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide bg-purple-600 text-white">
                            Max
                          </span>
                          <h4 className="font-bold text-xl text-slate-900 mt-2">{<WelcomePrice plan="max" price={planPrices.max} />}<span className="text-sm font-normal text-slate-500">{t('/month')}</span></h4>
                        </div>
                      </div>
                      <ul className="space-y-2 mb-4">
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('{count} videos per month', { count: 100 })}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('Up to 2 minutes per video')}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('All export formats')}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('Priority processing')}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('Priority support')}
                        </li>
                        <li className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckIcon />
                          {t('Dedicated account manager')}
                        </li>
                      </ul>
                      <button
                        onClick={() => handleUpgrade('max')}
                        disabled={isManagingSubscription}
                        className="w-full px-6 py-3 rounded-xl font-bold text-sm bg-purple-600 text-white hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t(isManagingSubscription ? 'Processing...' : 'Subscribe to Max')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Payment Methods Section */}
        <section className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
              <ShieldIcon />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{t('Payment Methods')}</h2>
              <p className="text-sm text-slate-500">{t('Manage your saved cards')}</p>
            </div>
          </div>

          <div className="p-6">
            {/* Setup Success Toast */}
            {setupSuccess && (
              <div className="mb-4 p-4 rounded-xl bg-green-50 border border-green-200 flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <CheckIcon />
                </div>
                <p className="text-sm font-semibold text-green-700">{t('Payment method added successfully!')}</p>
              </div>
            )}

            {isLoadingPaymentMethods ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Error Message */}
                {paymentMethodError && (
                  <div className="p-4 rounded-xl bg-red-50 border border-red-100">
                    <p className="text-sm text-red-600">{t(paymentMethodError)}</p>
                  </div>
                )}

                {/* Card List */}
                {paymentMethods.length > 0 ? (
                  <div className="space-y-3">
                    {paymentMethods.map((pm) => (
                      <div
                        key={pm.id}
                        className={`p-4 rounded-2xl border-2 transition-all ${
                          pm.is_default
                            ? 'border-[#f73140]/30 bg-[#f73140]/5'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {/* Card Brand Icon */}
                            <div className={`w-12 h-8 rounded-lg flex items-center justify-center text-xs font-bold uppercase tracking-wide ${
                              pm.brand === 'visa' ? 'bg-blue-600 text-white' :
                              pm.brand === 'mastercard' ? 'bg-orange-500 text-white' :
                              pm.brand === 'amex' ? 'bg-blue-400 text-white' :
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {pm.brand === 'visa' ? 'VISA' :
                               pm.brand === 'mastercard' ? 'MC' :
                               pm.brand === 'amex' ? 'AMEX' :
                               (pm.brand || '?').toUpperCase().slice(0, 4)}
                            </div>

                            {/* Card Details */}
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-slate-900">
                                  {t('{brand} ending in {last4}', { brand: CARD_BRAND_LABELS[pm.brand] || t('Card'), last4: pm.last4 })}
                                </span>
                                {pm.is_default && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-[#f73140] text-white">
                                    {t('Default')}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 mt-0.5">
                                {t('Expires {date}', { date: `${String(pm.exp_month).padStart(2, '0')}/${String(pm.exp_year).slice(-2)}` })}
                              </p>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            {!pm.is_default && (
                              <button
                                onClick={() => handleSetDefault(pm.id)}
                                disabled={settingDefaultId === pm.id}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all disabled:opacity-50"
                              >
                                {t(settingDefaultId === pm.id ? 'Setting...' : 'Set as default')}
                              </button>
                            )}

                            {/* Remove Button / Confirmation */}
                            {confirmRemoveId === pm.id ? (
                              <div className="flex flex-col items-end gap-1.5">
                                {paymentMethods.length === 1 && hasPaidPlan && (
                                  <p className="text-[11px] text-amber-600 font-semibold max-w-[200px] text-end">
                                    {t('Your subscription will fail to renew without a card')}
                                  </p>
                                )}
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => handleRemovePaymentMethod(pm.id)}
                                    disabled={removingPaymentMethodId === pm.id}
                                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 transition-all disabled:opacity-50"
                                  >
                                    {t(removingPaymentMethodId === pm.id ? 'Removing...' : 'Remove anyway')}
                                  </button>
                                  <button
                                    onClick={() => setConfirmRemoveId(null)}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-all"
                                  >
                                    {t('Cancel')}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmRemoveId(pm.id)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"
                                title={t('Remove card')}
                              >
                                <TrashIcon />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Empty State */
                  <div className="text-center py-8">
                    <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                      <CreditCardIcon />
                    </div>
                    <p className="font-semibold text-slate-700">{t('No payment methods saved')}</p>
                    <p className="text-sm text-slate-500 mt-1">{t('Add a card to manage your subscription')}</p>
                  </div>
                )}

                {/* Add Payment Method Button */}
                <button
                  onClick={handleAddPaymentMethod}
                  disabled={isAddingPaymentMethod}
                  className="w-full px-6 py-3.5 rounded-xl font-bold text-sm border-2 border-dashed border-slate-300 text-slate-600 hover:border-[#f73140] hover:text-[#f73140] hover:bg-[#f73140]/5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isAddingPaymentMethod ? (
                    <>
                      <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                      {t('Redirecting to Stripe...')}
                    </>
                  ) : (
                    <>
                      <PlusIcon />
                      {t('Add Payment Method')}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Language Section */}
        <section className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
                <GlobeIcon />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">{t('Language')}</h3>
                <p className="text-sm text-slate-500">{t('Choose the language of the interface')}</p>
              </div>
            </div>
            <LanguageSwitcher variant="segmented" />
          </div>
        </section>

        {/* Logout Section */}
        <section className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-6 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-slate-900">{t('Sign Out')}</h3>
              <p className="text-sm text-slate-500">{t('Sign out of your account')}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-5 py-2.5 rounded-xl font-bold text-sm border-2 border-red-200 text-red-600 hover:bg-red-50 transition-all"
            >
              {t('Sign Out')}
            </button>
          </div>
        </section>
      </main>

      {/* Subscription Confirmation Modal */}
      {showSubscriptionConfirmModal && selectedPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-[#f73140] to-[#ff4757]">
              <h3 className="text-xl font-bold text-white">{t('Confirm Subscription')}</h3>
              <p className="text-sm text-white/90 mt-1">{t('Review your subscription details')}</p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Plan Details */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-600">{t('Plan')}</span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                    selectedPlan === 'max' ? 'bg-purple-600 text-white' :
                    selectedPlan === 'pro' ? 'bg-[#f73140] text-white' :
                    'bg-blue-500 text-white'
                  }`}>
                    {getPlanDetails(selectedPlan).name}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-slate-900">{t(getPlanDetails(selectedPlan).price)}</span>
                  <span className="text-sm text-slate-500">{t(confirmedOfferPercent ? '/first month' : '/month')}</span>
                </div>
                <p className="text-sm text-slate-600 mt-2">
                  {t('{count} videos per month • 2 min max per video', { count: getPlanDetails(selectedPlan).videos })}
                </p>
              </div>

              {/* Payment Method */}
              {defaultPaymentMethod ? (
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-7 rounded-lg flex items-center justify-center text-xs font-bold uppercase ${
                        defaultPaymentMethod.brand === 'visa' ? 'bg-blue-600 text-white' :
                        defaultPaymentMethod.brand === 'mastercard' ? 'bg-orange-500 text-white' :
                        'bg-slate-300 text-slate-700'
                      }`}>
                        {defaultPaymentMethod.brand === 'visa' ? 'VISA' :
                         defaultPaymentMethod.brand === 'mastercard' ? 'MC' :
                         (defaultPaymentMethod.brand || '?').toUpperCase().slice(0, 4)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-900" dir="ltr">
                          •••• {defaultPaymentMethod.last4}
                        </p>
                        <p className="text-xs text-slate-500">
                          {t('Expires {date}', { date: `${String(defaultPaymentMethod.exp_month).padStart(2, '0')}/${String(defaultPaymentMethod.exp_year).slice(-2)}` })}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-green-600">✓ {t('Default')}</span>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-700">{t("You'll be redirected to Stripe to add a payment method.")}</p>
                </div>
              )}

              {/* Terms */}
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                <p className="text-xs text-blue-900 leading-relaxed">
                  {rich(
                    confirmedOfferPercent
                      ? t('By confirming, you agree to be charged <b>{price}</b> for your first month, then <b>{renewal}</b> monthly.', { price: t(getPlanDetails(selectedPlan).price), renewal: t(getPlanDetails(selectedPlan).renewalPrice) })
                      : t('By confirming, you agree to be charged <b>{price}</b> monthly.', { price: t(getPlanDetails(selectedPlan).price) }),
                    { b: (c) => <strong>{c}</strong> }
                  )}{' '}
                  {t('Your subscription will auto-renew each month. You can cancel anytime from Settings.')}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-3">
              <button
                onClick={() => {
                  setShowSubscriptionConfirmModal(false);
                  setSelectedPlan(null);
                }}
                disabled={isManagingSubscription}
                className="flex-1 px-4 py-3 rounded-xl font-bold text-sm border-2 border-slate-300 text-slate-700 hover:bg-white transition-all disabled:opacity-50"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleConfirmSubscription}
                disabled={isManagingSubscription}
                className="flex-1 px-4 py-3 rounded-xl font-bold text-sm bg-[#f73140] text-white hover:bg-[#d62331] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isManagingSubscription ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('Processing...')}
                  </>
                ) : (
                  <>
                    <ShieldIcon />
                    {t('Confirm & Subscribe')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
